<template>
  <div class="ActiveTab MergeTab">
    <LocatieInformatieHeader :chargingpoint="chargingpoint">
      <h4>Samenvoegen</h4>
    </LocatieInformatieHeader>

    <div
      v-if="hasPossibleDuplicates"
      class="ActiveTab__Content d-flex flex-column"
    >
      <p>
        We vermoeden dat deze locatie een duplicaat is. Als je deze locatie als duplicaat markeert, dan wordt deze samengevoegd met de andere locatie.
      </p>
      <b-form-group
        label="Samenvoegen met:"
      >
        <b-form-radio-group
          v-model="selected"
        >
          <div
            v-for="duplicate in possibleDuplicates"
            :key="duplicate.data.uuid"
            class="d-flex justify-content-between align-items-center"
          >
            <b-form-radio
              :value="duplicate.data.uuid"
            >
              <InlineChargingpoint :chargingpoint="duplicate" />
            </b-form-radio>
            <b-icon-x-lg
              v-if="duplicate.data.uuid !== selected"
              role="button"
              @click="selectReject(duplicate.data.uuid)"
            />
          </div>
        </b-form-radio-group>
      </b-form-group>
      <b-form-group
        v-if="selected"
        label="Coördinaten overnemen van:"
      >
        <b-form-radio-group
          v-model="useCoordinatesFrom"
        >
          <div
            class="d-flex justify-content-between align-items-center mb-2"
          >
            <b-form-radio
              :value="selected"
            >
              <InlineChargingpoint :chargingpoint="selectedChargingpoint">
                <div class="text-muted ml-4">
                  {{ selectedAddressSource }}
                </div>
              </InlineChargingpoint>
            </b-form-radio>
          </div>
          <div
            class="d-flex justify-content-between align-items-center"
          >
            <b-form-radio
              :value="chargingpoint.data.uuid"
            >
              <InlineChargingpoint :chargingpoint="chargingpoint">
                <div class="text-muted ml-4">
                  {{ thisAddressSource }}
                </div>
              </InlineChargingpoint>
            </b-form-radio>
          </div>
        </b-form-radio-group>
      </b-form-group>
      <b-form-textarea
        v-model="reason"
        class="mt-3"
        size="sm"
        placeholder="Geef optioneel een reden op"
        rows="1"
        max-rows="6"
      />
      <div class="mt-3 ml-auto">
        <b-button
          v-if="selected"
          size="sm"
          variant="secondary"
          @click="reset"
        >
          Annuleren
        </b-button>
        <b-button
          type="submit"
          size="sm"
          variant="primary"
          class="ml-2"
          :disabled="!(selected && useCoordinatesFrom)"
          @click="acceptMerge"
        >
          Markeer als duplicaat
        </b-button>
      </div>
    </div>

    <div
      v-else
      class="ActiveTab__Content"
    >
      <p>
        Samenvoegen is niet mogelijk, want deze locatie heeft geen
        vermoedelijke duplicaten. Indien dit wel het geval is, neem contact op
        met <MailtoSupport />
      </p>
    </div>

    <b-modal
      ref="confirm-reject"
      title="Duplicaat weghalen"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <p>
        Weet u zeker dat u deze markering als mogelijk duplicaat wilt weghalen? Hierna is het niet meer mogelijk om de locaties samen te voegen.
      </p>
      <template #modal-footer>
        <b-button
          variant="light"
          @click="cancelReject"
        >
          Annuleren
        </b-button>
        <b-button
          variant="primary"
          @click="confirmReject"
        >
          Bevestigen
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import InlineChargingpoint from '@/components/common/InlineChargingpoint'
import MailtoSupport from '@/components/common/MailtoSupport'
import { BIconXLg } from 'bootstrap-vue'
import { sourceToSlug } from '@/services/sourceTranslation'
import { checkStatus, returnJson } from '@/helpers/api'
import LocatieInformatieHeader from '@/components/map/sidebar/LocatieInformatieHeader'

export default {
  components: { LocatieInformatieHeader, BIconXLg, InlineChargingpoint, MailtoSupport },
  data() {
    return {
      reason: '',
      selected: '',
      useCoordinatesFrom: '',
      toBeRejected: '',
    }
  },
  computed: {
    ...mapGetters('planmode', {
      chargingpoint: 'getSelectedChargingpoint',
      possibleDuplicates: 'getPossibleDuplicatesOfSelectedChargingpoint',
    }),
    hasPossibleDuplicates() {
      return this.possibleDuplicates.length > 0
    },
    selectedChargingpoint() {
      return this.possibleDuplicates.find(duplicate => duplicate.data.uuid === this.selected)
    },
    thisAddressSource() {
      return sourceToSlug({ source: this.chargingpoint.data.address?.source })
    },
    selectedAddressSource() {
      return sourceToSlug({ source: this.selectedChargingpoint.data.address?.source })
    },
  },
  watch: {
    chargingpoint() {
      this.reset()
    },
  },
  methods: {
    ...mapActions('planmode', [
      'mergeChargingpoints',
      'removePossibleDuplicates',
      'showLinesToPossibleDuplicates',
    ]),
    reset() {
      this.selected = ''
      this.useCoordinatesFrom = ''
    },
    showModal() {
      this.$refs['confirm-reject'].show()
    },
    hideModal() {
      this.$refs['confirm-reject'].hide()
    },
    selectReject(uuid) {
      this.toBeRejected = uuid
      this.showModal()
    },
    cancelReject() {
      this.toBeRejected = ''
      this.hideModal()
    },
    confirmReject() {
      this.hideModal()
      this.rejectMerge(this.toBeRejected)
      this.toBeRejected = ''
    },
    async acceptMerge() {
      try {
        const token = await this.$auth.getTokenSilently()
        const result = await fetch('/api/mergeaccept', {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            chargingpoints: [
              this.chargingpoint.data.uuid,
              this.selected,
            ],
            useCoordinatesFrom: this.useCoordinatesFrom,
            reason: this.reason || undefined,
          }),
        })
          .then(checkStatus)
          .then(returnJson)

        this.mergeChargingpoints({
          chargingpoints: [
            this.chargingpoint.data.uuid,
            this.selected,
          ],
          result: result.chargingpoint,
        })
        this.$notify({
          type: 'success',
          title: 'Gelukt!',
          text: 'De locaties zijn samengevoegd.',
        })
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'Het samenvoegen van de locaties is niet gelukt. Probeer het later opnieuw.',
        })
      }

    },
    async rejectMerge(rejectedDuplicate) {
      try {
        const token = await this.$auth.getTokenSilently()
        await fetch('/api/mergereject', {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            chargingpoints: [
              this.chargingpoint.data.uuid,
              rejectedDuplicate,
            ],
          }),
        })
          .then(checkStatus)

        this.removePossibleDuplicates({
          uuid1: this.chargingpoint.data.uuid,
          uuid2: rejectedDuplicate,
        })
        this.showLinesToPossibleDuplicates()
        this.$notify({
          type: 'success',
          title: 'Gelukt!',
          text: 'Het duplicaat is weggehaald.',
        })
      } catch (err) {
        this.$notify({
          type: 'error',
          title: 'Er is iets misgegaan!',
          text: 'Het weghalen van het duplicaat is niet gelukt. Probeer het later opnieuw.',
        })
      }

    },
  },
}
</script>

<style scoped lang="scss">
.ActiveTab__Content {
  flex-grow: 1;
  p {
    margin-bottom: 0.5rem;
  }
}
</style>
